import { ArrayUtil, DebouncedFunction, VerticalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Component } from "../..";
import { Image } from "../../components/image/Image";
import { ImageProps } from "../../components/image/ImageProps";
import { Label } from "../../components/label/Label";
import { Panel } from "../../components/panel/Panel";
import { ReadMoreType } from "../../components/label/ReadMoreType";
import { SeparatorPanel } from "../SeparatorPanel";
import { Tooltip } from "./Tooltip";
import { TooltipContentResolver } from "./TooltipContentResolver";
import { TooltipSource } from "./TooltipSource";
import { TooltipSourceType } from "./TooltipSourceType";
import { TooltipResolvedContent } from "./TooltipResolvedContent";

export class TooltipFiller {
    static readonly leftPanelSources: TooltipSourceType[] = TooltipFiller.getLeftPanelSources();
    static readonly rightPanelSources: TooltipSourceType[] = TooltipFiller.getRightPanelSources();
    private canLayoutContent = false;
    private contentResolver: TooltipContentResolver;
    debouncedLayoutContent = DebouncedFunction.create(() => this.layoutContent(), 200);
    private resolvedContent: Map<TooltipSourceType, TooltipResolvedContent> = new Map();
    private _separatorPanel: SeparatorPanel;
    private _tooltip: Tooltip;

    constructor(tooltip: Tooltip) {
        this.tooltip = tooltip;
    }

    get tooltip(): Tooltip {
        return this._tooltip;
    }

    private set tooltip(value: Tooltip) {
        this._tooltip = value;
    }

    get anchorComponent(): Component {
        return this.tooltip.anchorComponent;
    }

    get sources(): Map<TooltipSourceType, TooltipSource> {
        return this.tooltip.sources;
    }

    private static getLeftPanelSources(): TooltipSourceType[] {
        return [
            TooltipSourceType.TOOLTIP_PROPERTY,
            TooltipSourceType.VALIDATION_WARNING,
            TooltipSourceType.DISABLED_TOOLTIP_PROPERTY,
        ].sort((a, b) => a - b);
    }

    private static getRightPanelSources(): TooltipSourceType[] {
        return [
            TooltipSourceType.DYNAMIC,
            TooltipSourceType.DYNAMIC_DESIGNER,
            TooltipSourceType.QUICK_INFO,
            TooltipSourceType.CURRENCY_EXCHANGE_INFO
        ].sort((a, b) => a - b);
    }

    async fillAndShow() {
        this.contentResolver = new TooltipContentResolver(this);
        await this.resolveSources();
        this.canLayoutContent = true;
        this.layoutContent();
    }

    private async resolveSources() {
        // Let all of the tooltip sources resolve, except for the currency exchange rate info source.  For that one,
        // wait until all of the other sources are resolved, and see if any of them include the exchange rate info
        // layout.  If none of them do, then let the currency exchange rate info layout be added to the tooltip.
        const nonCurrencyExchangeInfoPromises = Array.from(this.sources.values())
            .filter(source => source.type !== TooltipSourceType.CURRENCY_EXCHANGE_INFO)
            .map(source => {
                return this.contentResolver.resolveSource(this.anchorComponent, source);
            }
        );
        Promise.all(nonCurrencyExchangeInfoPromises).then(() => {
            this.addCurrencyExchangeInfoSource();
        });
    }

    private addCurrencyExchangeInfoSource() {
        // If the tooltip isn't supposed to include the exchange info layout, bail.  This will be the case in companies
        // where multi-currency is not in use, or when a field is not a currency field, etc.
        if (this.tooltip.includesCurrencyExchangeInfo !== true) {
            return;
        }
        let foundExchangeInfoLayout = false;
        for (const resolvedContent of this.resolvedContent.values()) {
            if (resolvedContent.includesCurrencyExchangeInfo) {
                foundExchangeInfoLayout = true;
                break;
            }
        }
        if (foundExchangeInfoLayout !== true) {
            const currencyExchangeInfoSource = this.sources.get(TooltipSourceType.CURRENCY_EXCHANGE_INFO);
            this.contentResolver.resolveSource(this.anchorComponent, currencyExchangeInfoSource);
        }
    }

    setResolvedContent(resolvedContent: TooltipResolvedContent) {
        this.resolvedContent.set(resolvedContent.type, resolvedContent);
    }

    private layoutContent() {
        // Safeguard in case one of the async/debounced calls to layoutContent occurs first (which shouldn't happen...)
        if (!this.canLayoutContent || this.tooltip.removedOrAnchorRemoved) {
            return;
        }
        this.tooltip.removeAll();
        this.tooltip.maxWidth = null;

        const leftPanelComponents = this.collectBuiltComponents(TooltipFiller.leftPanelSources);
        const rightPanelComponents = this.collectBuiltComponents(TooltipFiller.rightPanelSources);
        if (!ArrayUtil.isEmptyArray(leftPanelComponents) && !ArrayUtil.isEmptyArray(rightPanelComponents)) {
            this.adjustBusySpinnerMargin(leftPanelComponents, { marginRight: 8 });
            this.adjustBusySpinnerMargin(rightPanelComponents, { marginLeft: 8 });
            const leftPanel = this.buildLeftPanel(leftPanelComponents);
            const rightPanel = this.buildRightPanel(rightPanelComponents);
            this.tooltip.add(leftPanel, this.separatorPanel, rightPanel);
        } else if (!ArrayUtil.isEmptyArray(leftPanelComponents)) {
            // Limit simple, text-based tooltips to 200px wide.  Do this by searching the built components for anything
            // that is not a label, or that is a label that has a width set.  A simple text-based tooltip or disabled
            // tooltip would not produce such a component, so we know we can limit the overall tooltip width to 200px.
            let nonWidthLabelFound = false;
            leftPanelComponents.forEach(component => {
                if (!nonWidthLabelFound &&
                    (!(component instanceof Label) || component.width != null ||
                        component.maxWidth != null || component.minWidth != null)) {
                    nonWidthLabelFound = true;
                }
            });
            this.tooltip.add(...leftPanelComponents);
            this.tooltip.maxWidth = nonWidthLabelFound ? null : 200;
        } else {
            this.tooltip.add(...rightPanelComponents);
        }
        this.evaluateAllowNestedTooltips();

        // Showing the tooltip here accounts for any async content that comes out of a non-async method.  When that
        // happens, the tooltip may not have been shown yet when the content finally populates.  (Normally we'd expect
        // the content area to have displayed with the busy spinner, but it wouldn't if the content is from an async
        // source).
        this.tooltip.show();
    }

    private adjustBusySpinnerMargin(components: Component[], props: Partial<ImageProps>) {
        components
            .filter(component => component instanceof Image && component.name === ImageName.spinner)
            .forEach(component => component.setProps(props));
    }

    private collectBuiltComponents(sources: TooltipSourceType[]): Component[] {
        const result: Component[] = [];
        let sourcesUsed = 0;
        for (let x=0 ; x<sources.length ; x++) {
            const type = sources[x];
            const component = this.resolvedContent.get(type)?.component;
            if (component != null) {
                const captionComponent = this.buildCaptionLabel(type);
                if (captionComponent != null) {
                    captionComponent.marginTop = sourcesUsed > 0 ? 8 : null;
                    component.marginTop = 0;
                    result.push(captionComponent);
                }
                else if (sourcesUsed > 0) {
                    component.marginTop = 12;
                }
                result.push(component);
                sourcesUsed++;
            }
        }
        return result;
    }

    private buildCaptionLabel(type: TooltipSourceType): Label {
        let text: string;
        if (type === TooltipSourceType.VALIDATION_WARNING) {
            text = "Error:";
        } else if (type === TooltipSourceType.DISABLED_TOOLTIP_PROPERTY) {
            text = "Disabled:";
        }
        return text == null ? null : new Label({
            text,
            fontSize: "small",
            marginBottom: 0,
            paddingBottom: 2,
            height: 16,
            color: "component.palette.textbox.caption.color",
            allowSelect: false,
            readMoreType: ReadMoreType.NONE
        });
    }

    private buildLeftPanel(components: Component[]): Panel {
        return new Panel({
            components,
            fillHeight: true,
            id: this.tooltip.createInnerComponentId("leftPanel"),
            maxWidth: 200,
            rowBreak: false,
            verticalAlign: VerticalAlignment.TOP
        });
    }

    private buildRightPanel(components: Component[]): Panel {
        const result = new Panel({
            components,
            fillHeight: true,
            id: this.tooltip.createInnerComponentId("rightPanel"),
            verticalAlign: VerticalAlignment.TOP
        });
        return result;
    }

    private get separatorPanel(): SeparatorPanel {
        if (this._separatorPanel == null) {
            this._separatorPanel = new SeparatorPanel(this.tooltip, true, 98);
        }
        return this._separatorPanel;
    }

    private evaluateAllowNestedTooltips() {
        if (!this.tooltip.allowNestedTooltips) {
            this.tooltip.setPropOnChildren("allowTooltip", false);
        }
    }
}
